import React, { useState, useEffect, useRef, useMemo } from "react";
import { Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import swal from "sweetalert";
import CustomDatePicker from "../../components/CustomDatePicker";

const IndividualForm = ({
  openFromCustomerUpdate,
  authrztn,
  countryOptions,
  statusOptions,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email,
  setEmail,
  companyAddress,
  setCompanyAddress,
  country,
  setCountry,
  destination,
  setDestination,
  civilStatus,
  setCivilStatus,
  birthDate,
  setBirthDate,
  gender,
  setGender,
  mobileNumber,
  setMobileNumber,
  jobPosition,
  setJobPosition,
  tin,
  setTin,
  validated,
  months,
  years,
  yearRef,
  selectYear,
  setSelectYear,
  yearList,
  decreaseYear,
  increaseYear,
  generateYears,
}) => {
  const [emailError, setEmailError] = useState("");
  let isEditable = false;

  if (openFromCustomerUpdate === true) {
    isEditable = authrztn.includes("Customers-Edit") ? false : true;
  }

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    // Simple email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError("Invalid email address");
    } else {
      setEmailError(""); // Clear the error if the email is valid
    }
  };

  const handleResetToToday = (changeYear, changeMonth) => {
    const currentYear = new Date().getFullYear();

    setBirthDate(new Date());

    generateYears(); // Reset Year List

    changeYear(currentYear); // Set year

    changeMonth(
      months.indexOf(
        months[new Date().getMonth()], // Set month
      ),
    );
  };

  const handleDateChange = (date) => {
    if (!date) {
      setBirthDate(null);
      return;
    }

    const today = new Date();

    const isToday =
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate();

    if (isToday) {
      swal({
        icon: "warning",
        title: "Invalid Birth Date",
        text: "Birth date cannot be today's date. Please select a valid past date.",
      });

      return;
    }

    setBirthDate(date);
  };

  // Keep a stable reference to the latest generateYears function
  const generateYearsRef = useRef(generateYears);
  useEffect(() => {
    generateYearsRef.current = generateYears;
  }, [generateYears]);

  // Wrap CustomInput in useMemo and use local state to prevent react-datepicker
  // from overriding the user's backspaces on invalid intermediate dates.
  const CustomInput = useMemo(() => {
    const Input = React.forwardRef(
      (
        { value, onClick, onChange, onBlur, onFocus, onKeyDown, disabled },
        ref,
      ) => {
        // Local state allows free typing/backspacing without strict overrides
        const [localValue, setLocalValue] = useState(value || "");

        // Sync localValue with react-datepicker only when a valid date confirms
        useEffect(() => {
          setLocalValue(value || "");
        }, [value]);

        const handleChange = (e) => {
          const raw = e?.target?.value || "";

          // Remove any characters that aren't digits or slashes
          const cleaned = raw.replace(/[^\d/]/g, "");

          // Auto-format MM/DD/YYYY
          const next = cleaned
            .replace(/^(\d{2})(\d)/, "$1/$2")
            .replace(/^(\d{2}\/\d{2})(\d)/, "$1/$2");

          // Validate length up to MM/DD/YYYY
          if (/^(\d{0,2}|\d{2}\/\d{0,2}|\d{2}\/\d{2}\/\d{0,4})$/.test(next)) {
            setLocalValue(next); // Update the visual input instantly

            // Pass the formatted value down to react-datepicker
            e.target.value = next;
            if (onChange) {
              onChange(e);
            }
          }
        };

        return (
          <input
            type="text"
            className="form-control w-100"
            style={{ cursor: "text" }}
            onClick={(e) => {
              if (onClick) onClick(e);

              let dateOfBirth = new Date().getFullYear();
              if (value) {
                const parsed = new Date(value);
                // Extract year safely if value is a valid date
                if (!isNaN(parsed.getTime())) {
                  dateOfBirth = parsed.getFullYear();
                }
              }

              if (generateYearsRef.current) {
                generateYearsRef.current(dateOfBirth);
              }
            }}
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            value={localValue}
            ref={ref}
            placeholder="MM/DD/YYYY"
            disabled={disabled}
          />
        );
      },
    );

    Input.displayName = "CustomInput";
    return Input;
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (yearRef.current && !yearRef.current.contains(event.target)) {
        setSelectYear(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <div className="row">
        <div className="col-sm">
          <Form.Group className="mb-3">
            <Form.Label>
              First Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              disabled={isEditable}
              name="firstName"
              placeholder="Enter First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </Form.Group>
        </div>
        <div className="col-sm">
          <Form.Group className="mb-3">
            <Form.Label>
              Last Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Last Name"
              disabled={isEditable}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </Form.Group>
        </div>
        <div className="col-sm">
          <Form.Group className="mb-3">
            <Form.Label>
              Email Address <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter Email"
              disabled={isEditable}
              value={email}
              onChange={handleEmailChange}
              required
            />
            {emailError && <small className="text-danger">{emailError}</small>}
          </Form.Group>
        </div>
      </div>

      <div className="row">
        <div className="col-sm">
          <Form.Group className="mb-3">
            <Form.Label>
              Company Address <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={companyAddress}
              disabled={isEditable}
              onChange={(e) => setCompanyAddress(e.target.value)}
              required
            />
          </Form.Group>
        </div>
        <div className="col-sm">
          <Form.Group className="mb-3" controlId="basedCurrency">
            <Form.Label>Country</Form.Label>
            <Form.Select
              defaultValue="Philippines"
              disabled={isEditable}
              required
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            >
              <option disabled value="">
                Select Country
              </option>
              {countryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
        <div className="col-sm">
          <Form.Group className="mb-3" controlId="basedCurrency">
            <Form.Label>
              Destination <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              defaultValue="Philippines"
              disabled={isEditable}
              required
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            >
              <option disabled value="">
                Select Destination
              </option>
              <option value="Local">Local</option>
              <option value="Overseas">Overseas</option>
            </Form.Select>
          </Form.Group>
        </div>
      </div>

      <div className="row">
        <div className="col-sm">
          <Form.Group className="mb-3" controlId="basedCurrency">
            <Form.Label>Civil Status</Form.Label>
            <Form.Select
              value={civilStatus}
              disabled={isEditable}
              onChange={(e) => setCivilStatus(e.target.value)}
            >
              <option value="">Select Status</option>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
        <div className="col-sm">
          <Form.Group className="mb-3">
            <Form.Label>Date of Birth</Form.Label>
            <CustomDatePicker
              label={"Birthday"}
              selected={birthDate ? new Date(birthDate) : null}
              handleDateChange={handleDateChange}
              CustomInput={CustomInput}
              disabled={isEditable}
              validated={validated}
              iconTopOffset={"0.7rem"}
              isClearable={true}
            />
          </Form.Group>
        </div>
        <div className="col-sm">
          <Form.Group className="mb-3">
            <Form.Label>
              Gender <span className="text-danger">*</span>
            </Form.Label>
            <div className="d-flex align-items-center">
              <Form.Check
                disabled={isEditable}
                className="me-2"
                type="radio"
                label="Male"
                name="gender"
                id="genderMale"
                value="Male"
                required
                checked={gender && gender === "Male"}
                onChange={(e) => setGender(e.target.value)}
              />
              <Form.Check
                disabled={isEditable}
                className="me-2"
                type="radio"
                label="Female"
                name="gender"
                id="genderFemale"
                value="Female"
                required
                checked={gender && gender === "Female"}
                onChange={(e) => setGender(e.target.value)}
              />
            </div>
          </Form.Group>
        </div>
      </div>

      <div className="row">
        <div className="row">
          <div className="col-sm">
            <Form.Group className="mb-3">
              <Form.Label>
                Mobile No. <span className="text-danger">*</span>
              </Form.Label>
              <div className="input-group">
                <span className="input-group-text">+63</span>
                <Form.Control
                  disabled={isEditable}
                  type="text"
                  maxLength={10}
                  value={mobileNumber}
                  onKeyPress={(e) => {
                    // Allow only numbers
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  placeholder="Enter mobile number"
                />
              </div>
            </Form.Group>
          </div>
          <div className="col-sm"></div>
          <div className="col-sm"></div>
        </div>

        <div className="col-sm">
          <Form.Group className="mb-3">
            <Form.Label>Job Position</Form.Label>
            <Form.Control
              disabled={isEditable}
              type="text"
              value={jobPosition}
              onChange={(e) => setJobPosition(e.target.value)}
            />
          </Form.Group>
        </div>
        <div className="col-sm">
          <Form.Group className="mb-3">
            <Form.Label>TIN</Form.Label>
            <Form.Control
              disabled={isEditable}
              type="text"
              value={tin}
              onChange={(e) => setTin(e.target.value)}
            />
          </Form.Group>
        </div>
      </div>
    </>
  );
};

export default IndividualForm;
